import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query"
import type { ErrorOption } from "react-hook-form"

import { useGetCustomerQueryKey } from "@unlikelystudio/react-ecommerce-hooks"

import type { Nullish } from "~/@types/generics"
import { SHOPIFY_CUSTOMER_METAFIELDS_PARAMS } from "~/lib/api/constants"
import { requestNextApiEndpoint } from "~/lib/api/utils/request-endpoint"
import { useLocale } from "~/lib/i18n/hooks/useLocale"
import type { MetafieldInput } from "~/lib/shopify/constants/metafields"
import useOnMutationError from "~/hooks/useOnMutationError"

interface UseSetMetafieldsMutationParams {
  metafields: Omit<MetafieldInput, "ownerId">[]
  customerAccessToken: Nullish<string>
}

export default function useSetMetafields(
  mutationOptions?: UseMutationOptions<unknown, unknown, UseSetMetafieldsMutationParams>,
  setError?: (name?: string, error?: ErrorOption) => void
) {
  // remove from @tanstack package because the invalidateQueries doesn't get the new metafield value
  // we need to hard refresh to get the new metafield value
  const queryClient = useQueryClient()

  const onMutationError = useOnMutationError(setError)
  const locale = useLocale()
  const getCustomerQueryKey = useGetCustomerQueryKey()

  return useMutation<unknown, unknown, UseSetMetafieldsMutationParams>(
    async ({ metafields, customerAccessToken }: UseSetMetafieldsMutationParams) => {
      if (!customerAccessToken) {
        throw new Error("Customer access token is required")
      }

      return await requestNextApiEndpoint(SHOPIFY_CUSTOMER_METAFIELDS_PARAMS, {
        accessToken: customerAccessToken,
        payload: {
          locale,
          metafields,
        },
      })
    },
    {
      ...mutationOptions,
      onSuccess: (data, ...rest) => {
        queryClient.invalidateQueries({ queryKey: getCustomerQueryKey, exact: false })
        mutationOptions?.onSuccess?.(data, ...rest)
      },
      onError: (err, ...rest) => {
        onMutationError(err, rest, mutationOptions?.onError)
      },
    }
  )
}
